import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Autocomplete,
  TextField,
  DialogContent,
  DialogActions,
  FormHelperText,
  Chip
} from '@mui/material';
import { toast } from 'react-toastify';
import { API } from '../../../../api';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { popupCountActions } from '../../../../redux/slices/poupsCountsSlice';
import { getUserDetails } from '../../../../utils/orgName';
import useProfileDetails from '../../../../hooks/useProfileDetails';
import { userActions, userInfoUpdate } from '../../../../redux/slices/user-profile/userProfieSlice';

interface DeptOption {
  _id: string;
  name: string;
}

const OnboardingDepartment = () => {
  const { ftue, settings } = useProfileDetails();
  const dispatch = useAppDispatch();
  const userData = useAppSelector((state) => state.userProfile.data);
  const { orgId } = getUserDetails();

  const orgDepartments: DeptOption[] = userData?.orgDepartments ?? [];
  const userDepartments: string[] = Array.isArray(userData?.departments) ? (userData?.departments ?? []) : [];

  const [selectedIds, setSelectedIds] = useState<string[]>(userDepartments);
  const [options, setOptions] = useState<DeptOption[]>(orgDepartments);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ftueLoading, setFtueLoading] = useState(false);
  const [error, setError] = useState('');

  const isRequired = settings?.department === true;
  const popuplength = useAppSelector((state) => state.popupsCount.popuplength);
  const count = useAppSelector((state) => state.popupsCount.count);
  const nextPopUpOrNot = popuplength - count;

  useEffect(() => {
    setSelectedIds(Array.isArray(userData?.departments) ? (userData?.departments ?? []) : []);
  }, [userData?.departments]);

  useEffect(() => {
    if (orgDepartments.length > 0) {
      setOptions(orgDepartments);
      return;
    }
    const fetchOrgDepartments = async () => {
      try {
        const res = await API.getOrgName(orgId);
        const depts = res?.data?.data?.departments ?? res?.data?.departments ?? [];
        setOptions(Array.isArray(depts) ? depts : []);
      } catch {
        setOptions([]);
      }
    };
    fetchOrgDepartments();
  }, [orgId, orgDepartments.length]);

  const updateFtue = async () => {
    setFtueLoading(true);
    try {
      const { userId } = getUserDetails();
      const response = await API.updateUserFtue({ ftue: false, userId });
      if (response.status === 200) {
        dispatch(userActions.userFtueUpdate(0));
      }
    } catch (e) {
      toast.error('Your profile could not be saved. Try again.');
    } finally {
      setFtueLoading(false);
    }
  };

  const onSubmit = async () => {
    setError('');
    if (isRequired && selectedIds.length === 0) {
      setError('Please select at least one department');
      return;
    }

    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('departments', JSON.stringify(selectedIds));
      const response = await API.updateUserProfile({
        orgId,
        data: fd
      });
      if (response?.status === 200 && response?.data?.data) {
        dispatch(userInfoUpdate(response.data.data));
      }
      if (ftue && nextPopUpOrNot !== 1) {
        dispatch(popupCountActions.increment(0));
      }
      if (ftue && nextPopUpOrNot === 1) {
        await updateFtue();
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Departments could not be saved. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedDepts = options.filter((d) => selectedIds.includes(d._id));

  return (
    <>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Autocomplete
            multiple
            value={selectedDepts}
            onChange={(_, newValue: DeptOption[]) => {
              setSelectedIds(newValue.map((d) => d._id));
              setError('');
            }}
            options={options}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.name ?? '')}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Departments"
                placeholder="Select departments"
                error={!!error}
                size="small"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((opt, index) => (
                <Chip label={opt.name} size="small" {...getTagProps({ index })} />
              ))
            }
          />
          {error && <FormHelperText error>{error}</FormHelperText>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        {ftue && (
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={() => dispatch(popupCountActions.decrement(0))}
          >
            Previous
          </Button>
        )}
        <Button color="primary" variant="contained" fullWidth disabled={isSubmitting || ftueLoading} onClick={onSubmit}>
          {isSubmitting || ftueLoading
            ? 'Saving...'
            : nextPopUpOrNot === 1
            ? 'Done'
            : nextPopUpOrNot > 1
            ? 'Next'
            : 'Save'}
        </Button>
      </DialogActions>
    </>
  );
};

export default OnboardingDepartment;
